/**
 * End-to-end observability.
 *
 * Onboarding regressions are quiet: a learner's state stops hydrating, a policy
 * change starts denying a write, a report call slows from 300ms to 6s. Nothing
 * throws in a way anyone sees, and the funnel just sags. This module makes each
 * of those four things a measured, queryable fact:
 *
 *   - **latency**    — every instrumented call records `duration_ms`
 *   - **error rate** — `ok = false` with the error code that caused it
 *   - **RLS denials**— a `42501` / "row-level security" failure is flagged
 *                      `denied`, because that is a permissions regression, not
 *                      an outage, and the two need different responses
 *   - **hydration fallbacks** — a read that had to serve something other than
 *                      the learner's cloud record (empty cache, degraded pull)
 *
 * Writes go to `public.ops_events`, batched exactly like analytics: telemetry
 * must never cost a learner a frame or a round trip. Dropping a batch is
 * always preferable to blocking the UI.
 */
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "wekit_ops_session";
const FLUSH_DELAY_MS = 3000;
const MAX_BATCH = 30;

/** Broad buckets so a dashboard row means something operationally. */
export type OpsArea =
  | "auth"
  | "hydration"
  | "state_write"
  | "assessment"
  | "report"
  | "careers"
  | "payments"
  | "ai"
  | "other";

export type OpsEvent = {
  area: OpsArea;
  name: string;
  ok: boolean;
  duration_ms: number | null;
  error_code: string | null;
  error_message: string | null;
  denied: boolean;
  hydration_fallback: boolean;
  props: Record<string, unknown>;
};

type QueuedOpsEvent = OpsEvent & {
  user_id: string | null;
  session_id: string;
  path: string | null;
};

// ------------------------------------------------------------- error shape

type MaybePostgrestError = {
  code?: string | null;
  message?: string | null;
  status?: number | null;
  details?: string | null;
} | null | undefined;

/**
 * Is this failure a row-level-security denial rather than a fault?
 *
 * Postgres raises `42501` for insufficient privilege; PostgREST surfaces a
 * JWT/permission problem as `PGRST301` or a 401/403. Any of those means the
 * request was understood and refused — the signal we actually want to alert on
 * separately, since a bad policy denies *every* learner silently.
 */
export function isRlsDenial(err: unknown): boolean {
  const e = err as MaybePostgrestError;
  if (!e) return false;
  const code = String(e.code ?? "");
  if (code === "42501" || code === "PGRST301" || code === "PGRST116") {
    return code !== "PGRST116"; // "no rows" is not a denial
  }
  if (e.status === 401 || e.status === 403) return true;
  const msg = `${e.message ?? ""} ${e.details ?? ""}`.toLowerCase();
  return msg.includes("row-level security")
    || msg.includes("row level security")
    || msg.includes("permission denied")
    || msg.includes("violates row");
}

function errorCode(err: unknown): string | null {
  const e = err as MaybePostgrestError;
  if (!e) return null;
  if (e.code) return String(e.code);
  if (e.status) return `http_${e.status}`;
  return "unknown";
}

function errorMessage(err: unknown): string | null {
  if (!err) return null;
  const e = err as MaybePostgrestError;
  const msg = e?.message ?? String(err);
  return msg.slice(0, 300);
}

// ------------------------------------------------------------------- queue

function getSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `o_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return "no-session";
  }
}

let queue: QueuedOpsEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let sending = false;
let currentUser: string | null = null;

/** Attribute subsequent events to a learner (or to nobody after sign-out). */
export function setOpsUser(uid: string | null): void {
  currentUser = uid;
}

function schedule(delay = FLUSH_DELAY_MS) {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => { timer = null; void flushOps(); }, delay);
}

/** Push every buffered measurement. Never throws. */
export async function flushOps(): Promise<void> {
  if (timer) { clearTimeout(timer); timer = null; }
  if (sending || queue.length === 0) return;
  const batch = queue;
  queue = [];
  sending = true;
  try {
    const { error } = await supabase.from("ops_events").insert(batch as never);
    if (error) throw error;
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[ops] batch failed", err);
    }
  } finally {
    sending = false;
    if (queue.length) schedule();
  }
}

/** Record one measurement. Fire-and-forget. */
export function recordOps(event: Partial<OpsEvent> & { area: OpsArea; name: string }): void {
  try {
    queue.push({
      area: event.area,
      name: event.name,
      ok: event.ok ?? true,
      duration_ms: event.duration_ms ?? null,
      error_code: event.error_code ?? null,
      error_message: event.error_message ?? null,
      denied: event.denied ?? false,
      hydration_fallback: event.hydration_fallback ?? false,
      props: event.props ?? {},
      user_id: currentUser,
      session_id: getSessionId(),
      path: typeof window !== "undefined" ? window.location.pathname : null,
    });
    if (queue.length >= MAX_BATCH) void flushOps();
    else schedule();
  } catch {
    /* telemetry must never break a flow */
  }
}

/**
 * Time an operation and classify its outcome.
 *
 * Works for both throwing calls and Supabase's `{ data, error }` shape — pass
 * the result through and the error is picked up either way.
 */
export async function measure<T>(
  area: OpsArea,
  name: string,
  fn: () => Promise<T>,
  props: Record<string, unknown> = {},
): Promise<T> {
  const t0 = now();
  try {
    const result = await fn();
    const err = (result as { error?: unknown } | null)?.error;
    const duration = Math.round(now() - t0);
    if (err) {
      recordOps({
        area, name, ok: false, duration_ms: duration,
        error_code: errorCode(err), error_message: errorMessage(err),
        denied: isRlsDenial(err), props,
      });
    } else {
      recordOps({ area, name, ok: true, duration_ms: duration, props });
    }
    return result;
  } catch (err) {
    recordOps({
      area, name, ok: false, duration_ms: Math.round(now() - t0),
      error_code: errorCode(err), error_message: errorMessage(err),
      denied: isRlsDenial(err), props,
    });
    throw err;
  }
}

/**
 * A read served from something other than the learner's cloud record.
 *
 * This is the regression that costs the most and shows the least: the learner
 * sees an empty dashboard, we see a 200.
 */
export function recordHydrationFallback(name: string, props: Record<string, unknown> = {}): void {
  recordOps({ area: "hydration", name, ok: false, hydration_fallback: true, props });
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

// ------------------------------------------------------------------ alerting

/**
 * Alert thresholds — one definition, used by the admin dashboard and by the
 * `ops-alerts` scheduled function, so what you see is what pages you.
 */
export type AlertRule = {
  id: string;
  label: string;
  /** Which measured quantity this watches. */
  metric: "error_rate" | "p95_ms" | "denials" | "hydration_fallbacks";
  /** Area to watch, or null for everything. */
  area: OpsArea | null;
  /** Fire when the value goes above this. */
  threshold: number;
  /** Ignore windows with fewer events than this — small samples lie. */
  minEvents: number;
  hint: string;
};

export const ALERT_RULES: AlertRule[] = [
  {
    id: "onboarding_error_rate",
    label: "Onboarding error rate",
    metric: "error_rate", area: "state_write", threshold: 2, minEvents: 50,
    hint: "State writes are failing — answers may not be reaching the database.",
  },
  {
    id: "hydration_p95",
    label: "Hydration p95 latency",
    metric: "p95_ms", area: "hydration", threshold: 2500, minEvents: 20,
    hint: "Sign-in state pull is slow; learners will see empty screens first.",
  },
  {
    id: "hydration_fallbacks",
    label: "Hydration fallbacks",
    metric: "hydration_fallbacks", area: "hydration", threshold: 5, minEvents: 1,
    hint: "Learners are being served state that did not come from the cloud.",
  },
  {
    id: "rls_denials",
    label: "RLS denials",
    metric: "denials", area: null, threshold: 10, minEvents: 1,
    hint: "A policy is refusing legitimate requests — check the newest migration.",
  },
  {
    id: "report_p95",
    label: "Report p95 latency",
    metric: "p95_ms", area: "report", threshold: 5000, minEvents: 20,
    hint: "Results generation is slowing; the post-assessment wait is growing.",
  },
  {
    id: "global_error_rate",
    label: "Overall error rate",
    metric: "error_rate", area: null, threshold: 5, minEvents: 100,
    hint: "Something broad is failing across the app.",
  },
  // Two edge functions carry the product: `ai-coach` (YoMA streams) and
  // `report-assemble` (the Living Report™). Both have failed silently before —
  // a 500 shows up as a generic toast, a slow stream as "YoMA is thinking".
  {
    id: "ai_coach_error_rate",
    label: "YoMA (ai-coach) failure rate",
    metric: "error_rate", area: "ai", threshold: 5, minEvents: 20,
    hint: "ai-coach is returning 5xx or empty streams — YoMA will look mute.",
  },
  {
    id: "ai_coach_p95",
    label: "YoMA stream p95 latency",
    metric: "p95_ms", area: "ai", threshold: 20000, minEvents: 20,
    hint: "Streams are taking too long to complete; learners abandon the chat.",
  },
  {
    id: "report_error_rate",
    label: "Report assemble failure rate",
    metric: "error_rate", area: "report", threshold: 2, minEvents: 20,
    hint: "report-assemble is failing — Living Report™ pages will be blank.",
  },
];

export type HealthRow = {
  area: string;
  events: number;
  failures: number;
  error_rate: number;
  denials: number;
  hydration_fallbacks: number;
  avg_ms: number | null;
  p95_ms: number | null;
  max_ms: number | null;
};

export type AlertVerdict = {
  rule: AlertRule;
  value: number;
  events: number;
  firing: boolean;
};

/** Aggregate the per-area rows the way a rule looks at them. */
function fold(rows: HealthRow[], area: OpsArea | null) {
  const scoped = area ? rows.filter((r) => r.area === area) : rows;
  const events = scoped.reduce((n, r) => n + Number(r.events || 0), 0);
  const failures = scoped.reduce((n, r) => n + Number(r.failures || 0), 0);
  return {
    events,
    failures,
    error_rate: events ? (failures / events) * 100 : 0,
    denials: scoped.reduce((n, r) => n + Number(r.denials || 0), 0),
    hydration_fallbacks: scoped.reduce((n, r) => n + Number(r.hydration_fallbacks || 0), 0),
    // The worst area's p95 is the one learners feel — averaging would hide it.
    p95_ms: scoped.reduce((m, r) => Math.max(m, Number(r.p95_ms || 0)), 0),
  };
}

/** Evaluate every rule against a health snapshot. Pure — trivially testable. */
export function evaluateAlerts(rows: HealthRow[]): AlertVerdict[] {
  return ALERT_RULES.map((rule) => {
    const f = fold(rows, rule.area);
    const value = Number(f[rule.metric] ?? 0);
    return {
      rule,
      value: Math.round(value * 100) / 100,
      events: f.events,
      firing: f.events >= rule.minEvents && value > rule.threshold,
    };
  });
}

if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushOps();
  });
  window.addEventListener("pagehide", () => { void flushOps(); });
}
